/**
 * **C03 · The closeout report per area** (Stream C, Documents.dc.html): "How did it go?" — one
 * letter page per area, to the chair and the directors. Its figures are Money's area closeout
 * (`GET /money/reports/area-closeout`, each part from its own module: the budget's lines, the
 * ledger's receipts, the area's counted hours, what the area used, the chair's closeout). This
 * lays them out as drawn; the margin and receipts per volunteer hour are the only arithmetic, and
 * they are ratios of figures the report hands over. The receipts budget is the budget's 4xxx lines,
 * or the directors' receipts line when the area has none; the watch band is the area's division's.
 * What the report does not hold prints NO DATA and names the WORKLIST row it waits on.
 */
import { dollars } from './dollars.js';
import { NO_DATA, type PaperCell, type PaperPage } from './paper_page.js';

type Cell = string | number | null;
/** The area closeout's sections as Money serves them. */
export interface CloseoutSection {
  head: string;
  rows: Cell[][];
}

export interface CloseoutInput {
  /** The fiscal year closing the festival held the September before its June 30. */
  fiscalYear: number;
  /** The report's title (`How did it go? · Pub · FY2027`) and sections; null when it did not answer. */
  report: { title: string; sections: CloseoutSection[] } | null;
  /** The day this page was prepared, as printed (`Sep 20, 2026`). */
  prepared: string;
}

const thousands = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const num = (c: Cell | undefined): number | null => (typeof c === 'number' ? c : null);
const fig = (c: number | null): PaperCell => c ?? NO_DATA;
/** `+ 3.0%` · `− 2.5%` — receipts against their budget. */
const pctVs = (actual: number | null, budget: number | null): string => {
  if (actual === null || budget === null || budget === 0) return NO_DATA;
  const p = ((actual - budget) / budget) * 100;
  return `${p < 0 ? '−' : '+'} ${Math.abs(p).toFixed(1)}%`;
};
/** `+ $300` · `− $190` — a cost line against its budget, in whole dollars as drawn. */
const dollarsVs = (spent: number, line: number): string => {
  const d = spent - line;
  return `${d < 0 ? '−' : '+'} ${dollars(Math.round(Math.abs(d) / 100) * 100).replace(/\.00$/, '')}`;
};

export function closeoutPage(i: CloseoutInput): PaperPage {
  const sec = (head: string): CloseoutSection | undefined =>
    i.report?.sections.find((s) => s.head === head);
  const area = /^How did it go\? · (.+) · FY\d+$/.exec(i.report?.title ?? '')?.[1] ?? NO_DATA;
  const festival = String(i.fiscalYear - 1);
  const next = String(i.fiscalYear);

  // The budget's lines: receipts are the 4xxx accounts, the rest are what the area spent.
  const lines = (sec('Budget against spent')?.rows ?? []).map((r) => ({
    label: String(r[0] ?? ''),
    line: num(r[1]) ?? 0,
    spent: num(r[2]) ?? 0,
  }));
  const income = lines.filter((l) => l.label.startsWith('4'));
  const costs = lines.filter((l) => !l.label.startsWith('4'));
  const receiptRows = sec('Receipts')?.rows ?? [];
  const receipts = num(receiptRows[0]?.[1]);
  // The budget's 4xxx lines when it has them; else the directors' receipts line (M17).
  const receiptsBudget = income.length
    ? income.reduce((s, l) => s + l.line, 0)
    : num(receiptRows.find((r) => r[0] === 'The receipts line, this area')?.[1]);
  const bandSec = sec('Watch band');
  const bandRow = bandSec?.rows[0];
  const band = !bandSec
    ? `watch band ${NO_DATA} (WORKLIST M17)`
    : bandRow
      ? `watch band ${String(bandRow[0])} – ${String(bandRow[1])}%${bandRow[2] === 'ratified' ? '' : ' (DRAFT)'}`
      : 'no watch band for this area';
  const spent = costs.reduce((s, l) => s + l.spent, 0);

  const people = sec('People')?.rows ?? [];
  const said = (what: string): string | undefined => {
    const v = people.find((r) => r[0] === what)?.[1];
    return typeof v === 'string' ? v : undefined;
  };
  const hoursSaid = said('Volunteer hours');
  const hours = hoursSaid && /^\d+$/.test(hoursSaid) ? Number(hoursSaid) : null;
  const chairs = said('Chairs');

  const closeout = sec('The closeout')?.rows ?? [];
  const says = (what: string): string =>
    String(closeout.find((r) => r[0] === what)?.[1] ?? NO_DATA).replace(/^[A-Z ]+ · /, '');
  const change = sec('To change for next year')?.rows[0]?.[0];

  return {
    title: `${area} · how did it go? · ${festival}`,
    sub: `${chairs && chairs !== NO_DATA ? `${chairs}, chairs` : `Chairs ${NO_DATA}`} · prepared ${i.prepared}`,
    blocks: [
      {
        head: 'RECEIPTS',
        cols: '1fr 100px 100px 100px',
        rows: [
          ['', 'BUDGET', 'ACTUAL', 'VS'],
          [
            income.length === 1 ? (income[0]?.label ?? '') : `${area} · receipts`,
            fig(receiptsBudget),
            fig(receipts),
            pctVs(receipts, receiptsBudget),
          ],
          [`assumption: ${NO_DATA} (WORKLIST M10)`, '', NO_DATA, 'units, not price'],
        ],
      },
      {
        head: 'COST LINES',
        cols: '1fr 100px 100px 100px',
        rows: [
          ...(costs.length
            ? costs.map((l) => [l.label, l.line, l.spent, dollarsVs(l.spent, l.line)])
            : [[NO_DATA, '', '', '']]),
          [
            'MARGIN',
            '',
            receipts ? `${(((receipts - spent) / receipts) * 100).toFixed(1)}%` : NO_DATA,
            `${band} · at ${receipts ? `${String(Math.round((spent / receipts) * 100))}%` : NO_DATA}`,
          ],
        ],
      },
      {
        head: 'PEOPLE AND THINGS',
        cols: '1fr 1fr',
        rows: [
          [
            'Volunteer hours',
            hours === null
              ? NO_DATA
              : `${thousands(hours)} · ${NO_DATA} people · ${NO_DATA} no-shows`,
          ],
          [
            'Receipts per volunteer hour',
            receipts !== null && hours ? Math.round(receipts / hours) : NO_DATA,
          ],
          ...(sec('What we used')?.rows ?? []).map((r) => [
            `${String(r[0] ?? '')} used`,
            `${String(r[2] ?? 0)} of ${String(r[1] ?? 0)}${num(r[3]) ? ` · ${String(r[3])} wasted` : ''}`,
          ]),
          [
            'Radios · keys',
            `radios ${says('Radios in')} · keys ${says('Supplies returned · keys in')}`,
          ],
        ],
      },
      {
        head: 'THE CHAIR’S NOTES',
        cols: '1fr',
        rows: [[`${NO_DATA} · the chair’s debrief is not built yet (WORKLIST K6)`]],
      },
      {
        head: `TO CHANGE FOR ${next}`,
        cols: '1fr',
        rows: [[typeof change === 'string' ? change : NO_DATA]],
      },
    ],
    foot: `Closeout · ${area} · the notes feed the ${next} baseline`,
  };
}
